import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import {
  MapLayer,
  MapLayerDetail,
  MapLayers,
  MapLayersDetails,
} from 'features/mapping/mapping.constants';
import { useAuthState } from 'state/useAuthState';
import { useLightningLayerEnabled } from 'features/mapping/hooks/useLightningLayerEnabled';
import { useWaterLevelEnabled } from 'features/waterLevel/hooks/useWaterLevelEnabled';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { ExternalFeatureLayer } from 'features/mapping/mapping.types';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { CommandPaletteAction } from 'components/CommandPaletteDialog/commandPaletteAction.types';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import {
  initialMapLayersState,
  useMapLayersState,
} from '../../state/useMapLayersState';
import {
  canUserAccessMapLayer,
  trackMapLayerToggleToPosthog,
} from '../../features/mapping/mapping.utils';

// Layers that live in MapLayersDetails but aren't user-toggleable here.
// Radar only shows in timeline mode (useMapMode / TimelineModeControl), it isn't
// a layer toggle. It's excluded from the Main map's allowedLayers, so toggling
// its visibility is a no-op.
const NON_TOGGLE_LAYERS: MapLayer[] = [MapLayers.RADAR];

// A palette entry for team layers: `title` is the label; `layers` all toggle together.
type TeamLayerToggleGroup = {
  title: string;
  layers: [MapLayer, MapLayerDetail][];
};

export const useMapLayerCommandPaletteActions = (): void => {
  const { t } = useTranslation();
  const mapLayersState = useMapLayersState();
  const { showMembershipFeatures, showMembershipProFeatures, permissions } =
    useAuthState();
  const { isInternalUser } = permissions;
  const { showLightningLayer } = useLightningLayerEnabled();
  const showWaterLevel = useWaterLevelEnabled();
  const isHeatDetectionsGisEnabled = useReleaseFlag(
    'show_heat_detections_from_gis_in_frontend',
  );
  const isNgfsDetectionsEnabled = useReleaseFlag(
    'show_ngfs_detections_in_frontend',
  );

  const accessibleLayers = useMemo(() => {
    // Layers only surfaced when a flag/toggle is enabled. Without this guard
    // their shortcut (e.g. `l v`) would toggle (and persist) a hidden layer.
    const layerConditionallyEnabled: Partial<Record<MapLayer, boolean>> = {
      [MapLayers.LIGHTNING]: showLightningLayer,
      [MapLayers.LANDSAT]: isHeatDetectionsGisEnabled,
      [MapLayers.NGFS_DETECTIONS]: isNgfsDetectionsEnabled,
      [MapLayers.WATER_LEVEL]: showWaterLevel,
    };

    return Object.entries(MapLayersDetails).filter(
      ([layerName, layerDetails]) => {
        if (NON_TOGGLE_LAYERS.includes(layerName as MapLayer)) {
          return false;
        }

        if (layerDetails.internalOnly && !isInternalUser) {
          return false;
        }

        if (
          layerName in layerConditionallyEnabled &&
          !layerConditionallyEnabled[layerName as MapLayer]
        ) {
          return false;
        }

        return canUserAccessMapLayer({
          minMembership: layerDetails.minMembership,
          requiredPermissions: layerDetails.requiredPermissions,
          showMembershipFeatures,
          showMembershipProFeatures,
          permissions,
        });
      },
    );
  }, [
    isInternalUser,
    permissions,
    showMembershipFeatures,
    showMembershipProFeatures,
    showLightningLayer,
    showWaterLevel,
    isHeatDetectionsGisEnabled,
    isNgfsDetectionsEnabled,
  ]);

  // External org/team feature layers are dynamic (not in MapLayersDetails).
  // Mirror the layers drawer: a parent with children is toggleable as a group
  // (all children together); its children are individually listed only when
  // the parent doesn't `treatGroupAsSingleLayer`. Pro-gated like the drawer.
  const { externalFeatureLayers } = useExternalFeatureLayers();
  const teamLayerGroups = useMemo<TeamLayerToggleGroup[]>(() => {
    const canAccess = canUserAccessMapLayer({
      minMembership: 'pro',
      showMembershipFeatures,
      showMembershipProFeatures,
    });
    if (!canAccess) {
      return [];
    }
    const toEntry = (
      layer: ExternalFeatureLayer,
      subtitle = '',
    ): [MapLayer, MapLayerDetail] => [
      layer.mapLayerId,
      {
        title: layer.displayName,
        subtitle,
        requiresWebGL2: false,
        minMembership: 'pro',
      },
    ];

    return externalFeatureLayers
      .filter((layer) => !layer.parentFeatureLayer)
      .flatMap((parent): TeamLayerToggleGroup[] => {
        if (!parent.hasChildLayers) {
          return [{ title: parent.displayName, layers: [toEntry(parent)] }];
        }
        const children = externalFeatureLayers.filter(
          (layer) => layer.parentFeatureLayer === parent.id,
        );
        if (children.length === 0) {
          return [];
        }
        const groupEntry: TeamLayerToggleGroup = {
          title: parent.displayName,
          layers: children.map((child) => toEntry(child)),
        };
        if (parent.treatGroupAsSingleLayer) {
          return [groupEntry];
        }
        // Children are flat in the palette (no nesting like the drawer), so show
        // the parent's name as the subtitle to aid search and disambiguate duplicate names.
        return [
          groupEntry,
          ...children.map((child) => ({
            title: child.displayName,
            layers: [toEntry(child, parent.displayName)],
          })),
        ];
      });
  }, [
    externalFeatureLayers,
    showMembershipFeatures,
    showMembershipProFeatures,
  ]);

  // Every team layer reachable from a toggle above, deduped (children of expandable
  // groups appear both in the group and on their own). Used by the reset commands.
  const teamLayerLeaves = useMemo<[MapLayer, MapLayerDetail][]>(() => {
    const seen = new Set<MapLayer>();
    return teamLayerGroups
      .flatMap((group) => group.layers)
      .filter(([layerName]) => {
        if (seen.has(layerName)) {
          return false;
        }
        seen.add(layerName);
        return true;
      });
  }, [teamLayerGroups]);

  const toggleableLayers = useMemo(
    () => [...accessibleLayers, ...teamLayerLeaves],
    [accessibleLayers, teamLayerLeaves],
  );

  // `externalFeatureLayers` is a fresh array each render, so pass a stable
  // content signature to the registration deps below — otherwise the shortcuts
  // re-register every render and loop.
  const teamLayerKey = externalFeatureLayers
    .map(
      (layer) =>
        `${layer.mapLayerId}:${layer.displayName}:${layer.parentFeatureLayer ?? ''}:${layer.hasChildLayers ? 1 : 0}:${layer.treatGroupAsSingleLayer ? 1 : 0}`,
    )
    .join('|');

  // Group static toggleable layers into toggle targets.
  // Layers sharing a `keyboardShortcut` toggle together under one `l <key>`.
  // Layers without one key on their (unique) name, so each becomes its own palette-only group.
  const layerToggleGroups = useMemo(() => {
    const groups = new Map<string, [MapLayer, MapLayerDetail][]>();
    for (const [layerName, layerDetails] of accessibleLayers) {
      const groupKey = layerDetails.keyboardShortcut ?? layerName;
      const entry: [MapLayer, MapLayerDetail] = [
        layerName as MapLayer,
        layerDetails,
      ];
      groups.get(groupKey)?.push(entry) ?? groups.set(groupKey, [entry]);
    }
    return [...groups.values()];
  }, [accessibleLayers]);

  const getLayerSubtitle = (details: MapLayerDetail): string =>
    'subtitleKey' in details ? t(details.subtitleKey) : details.subtitle;

  // Builds a toggle entry for one or more layers.
  // When `keys` is omitted the entry is command-palette-only (no bound keyboard shortcut).
  // `label` overrides the default comma-joined layer titles (e.g. a team layer group's parent name).
  const buildLayerToggleHandler = (
    layerNamesAndDetails: [MapLayer, MapLayerDetail][],
    keys?: string,
    label?: string,
  ): CommandPaletteAction => {
    const description = t('keyboardShortcuts.mapLayer.toggleVisibility', {
      layers:
        label ??
        layerNamesAndDetails
          .map((d) => ('titleKey' in d[1] ? t(d[1].titleKey) : d[1].title))
          .join(', '),
      // escapeValue: false keeps titles with `&` from being escaped
      interpolation: { escapeValue: false },
    });

    // External/team layers are synthesized with `title`, static layers use `titleKey`.
    // Group them under their own palette category.
    const isTeamLayer = 'title' in layerNamesAndDetails[0][1];

    // Computed at registration time; stays fresh because these actions re-register
    // whenever mapLayersState.mapLayers changes as part of dependencies.
    const anySelected = layerNamesAndDetails.some((detail) =>
      mapLayersState.mapLayers.includes(detail[0]),
    );

    return {
      category: isTeamLayer ? 'teamLayers' : 'mapLayers',
      description,
      subtitle:
        layerNamesAndDetails.length === 1
          ? getLayerSubtitle(layerNamesAndDetails[0][1])
          : undefined,
      keys,
      isActive: anySelected,
      handler: () => {
        for (const [layerName] of layerNamesAndDetails) {
          mapLayersState.setMapLayerVisibility(layerName, !anySelected);
          trackMapLayerToggleToPosthog(layerName, !anySelected);
        }

        const confirmTranslationKey = anySelected
          ? 'keyboardShortcuts.confirmation.off'
          : 'keyboardShortcuts.confirmation.on';

        enqueueSnackbar(
          t(confirmTranslationKey, {
            layer: description,
            interpolation: { escapeValue: false },
          }),
          { variant: 'info', autoHideDuration: 1000 },
        );
      },
    };
  };

  const handlers: Array<CommandPaletteAction> = [
    ...layerToggleGroups.map((layers) => {
      const shortcut = layers[0][1].keyboardShortcut;
      return buildLayerToggleHandler(layers, shortcut && `l ${shortcut}`);
    }),
    ...teamLayerGroups.map((group) =>
      buildLayerToggleHandler(group.layers, undefined, group.title),
    ),
  ];

  // Reset (`l 0`) clears every layer the user can toggle here, whether it has a
  // bound shortcut or is command-palette-only.
  const allToggleableLayerNames = useMemo(
    () => toggleableLayers.map(([layerName]) => layerName as MapLayer),
    [toggleableLayers],
  );

  // Create a reset command to turn off all layers that can be toggled here
  const resetAllHandler: CommandPaletteAction = {
    category: 'mapLayers',
    description: t('keyboardShortcuts.mapLayer.resetAll'),
    keys: 'l 0',
    handler: () => {
      for (const layerName of allToggleableLayerNames) {
        mapLayersState.setMapLayerVisibility(layerName, false);
      }
      // Tracking this as a single event to avoid hammering posthog.
      trackMapLayerToggleToPosthog('resetAllMapLayers', true);

      enqueueSnackbar(t('keyboardShortcuts.confirmation.resetAll'), {
        variant: 'info',
        autoHideDuration: 1000,
      });
    },
  };

  // Reset to default (`l 1`) restores each toggleable layer to the base set a new user starts with (initialMapLayersState).
  const resetToDefaultHandler: CommandPaletteAction = {
    category: 'mapLayers',
    description: t('keyboardShortcuts.mapLayer.reset'),
    keys: 'l 1',
    handler: () => {
      for (const layerName of allToggleableLayerNames) {
        mapLayersState.setMapLayerVisibility(
          layerName,
          Boolean(initialMapLayersState[layerName]),
        );
      }
      // Tracking this as a single event to avoid hammering posthog.
      trackMapLayerToggleToPosthog('resetMapLayersToDefault', true);

      enqueueSnackbar(t('keyboardShortcuts.mapLayer.reset'), {
        variant: 'info',
        autoHideDuration: 1000,
      });
    },
  };

  useKeyboardShortcuts(
    [...handlers, resetAllHandler, resetToDefaultHandler],
    [
      mapLayersState.mapLayers,
      showMembershipFeatures,
      showMembershipProFeatures,
      isInternalUser,
      showLightningLayer,
      showWaterLevel,
      isHeatDetectionsGisEnabled,
      isNgfsDetectionsEnabled,
      teamLayerKey,
    ],
  );
};
